import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@progress/kendo-react-buttons';
import {
  Scheduler,
  DayView,
  WeekView,
  MonthView,
  AgendaView,
  SchedulerItem,
} from '@progress/kendo-react-scheduler';
import { createContent, deleteContent, updateContent } from '../../actions';
import CalendarEventForm from '../../components/CalendarEventForm';
import { contentToEvent, eventToContent } from '../../utils/eventMapper';

const CONTENT_TYPES = ['Event', 'Post', 'News'];

const MODEL_FIELDS = {
  id: 'id',
  title: 'title',
  description: 'description',
  start: 'start',
  end: 'end',
  isAllDay: 'isAllDay',
  recurrenceRule: 'recurrenceRule',
  recurrenceId: 'recurrenceId',
  recurrenceExceptions: 'recurrenceExceptions',
};

const noop = () => {};

const CalendarItem = (props) => (
  <SchedulerItem
    {...props}
    style={props.dataItem.expired
      ? { ...props.style, backgroundColor: '#d32f2f', borderColor: '#d32f2f', color: '#fff' }
      : props.style}
  />
);

function DashboardCalendar() {
  const dispatch = useDispatch();

  const { content } = useSelector(state => state.contentReducer);
  const { token, user } = useSelector(state => state.authReducer);

  const [selectedTypes, setSelectedTypes] = useState(['Event']);

  const canEdit = !user.viewOnly && !user.notificationOnly;

  const events = content
    .filter(item => selectedTypes.includes(item.objectType))
    .map(contentToEvent);

  const toggleType = (type) => {
    setSelectedTypes(current => current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type]);
  };

  // Records belonging to another site are read-only, matching Content.jsx and EditCommandCell.
  const ownedByUser = (item) => !item.site || item.site.id === user.site.id;

  const handleDataChange = ({ created, updated, deleted }) => {
    created.forEach((event) => {
      dispatch(createContent({
        ...eventToContent(event),
        objectType: event.objectType || 'Event',
        siteId: user.site.id,
        user: user,
      }, token, noop));
    });

    updated.filter(ownedByUser).forEach((event) => {
      dispatch(updateContent(event.id, eventToContent(event), token, noop));
    });

    // Kendo already confirms removals via its own dialog, so no second prompt here.
    deleted.filter(ownedByUser).forEach((event) => {
      dispatch(deleteContent(event.id, token, noop));
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.toolbar}>
        {CONTENT_TYPES.map(type => (
          <Button
            key={type}
            togglable={true}
            selected={selectedTypes.includes(type)}
            onClick={() => toggleType(type)}
          >
            {type}
          </Button>
        ))}
      </div>
      <Scheduler
        data={events}
        modelFields={MODEL_FIELDS}
        item={CalendarItem}
        defaultView={'month'}
        height={'calc(100vh - 130px)'}
        editable={canEdit && {
          add: true,
          edit: true,
          remove: true,
          drag: true,
          resize: true,
        }}
        form={CalendarEventForm}
        onDataChange={handleDataChange}
      >
        <DayView currentTimeMarker={false} />
        <WeekView currentTimeMarker={false} />
        <MonthView />
        <AgendaView />
      </Scheduler>
    </div>
  );
}

const styles = {
  container: {
    width: '88vw',
    padding: '0px 15px',
  },
  toolbar: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    minHeight: 40,
    paddingBottom: 5,
  },
};

export default DashboardCalendar;
